import { z } from 'zod';

import {
  type AnswerReading,
  answerListLength,
  readAnswerDocument,
  refusedShape,
  unfencedAnswer,
} from './answer-document.ts';
import { type AnswerWire, answerWire } from './answer-wire.ts';
import type {
  ArtifactStatus,
  ResearchQuestion,
  RqsContent,
  RqsGoalRef,
  StalenessWindow,
} from './artifacts.ts';
import { type ArtifactChangeNotes, reconciledVersionText } from './change-notes.ts';
import { DomainError } from './errors.ts';
import { studyTypeIdSchema } from './study-types.ts';
import type { ArtifactVersionReason } from './version-reasons.ts';

export {
  ARTIFACT_VERSION_REASONS,
  artifactVersionReasonSchema,
  assertVersionReason,
  parseArtifactVersionReason,
  type ArtifactVersionReason,
} from './version-reasons.ts';

/**
 * The queue research questions are drafted on. The API process stores the request and enqueues it;
 * the worker, which is the only process holding a model, answers it.
 */
export const QUEUE_RQS_DRAFT = 'rqs.draft';

/**
 * Placeholder policy values for one research question: about one sentence of question and one of
 * the decision it informs. Product owns both.
 */
export const MAX_RQ_TEXT_CHARS = 300;
export const MAX_RQ_DECISION_CHARS = 200;

/** The longest note a reshape carries: what the researcher asked for, in a sentence or two. */
export const MAX_RQS_NOTE_CHARS = 500;

/**
 * How often the Questions step re-reads a pending request, and how long it waits before it offers
 * "Try again". Placeholder policy values, the same pair the Guide step polls on: the wait leaves a
 * draft room for a second call when its first stalls.
 */
export const RQS_DRAFT_POLL_MS = 1000;
export const RQS_DRAFT_WAIT_MS = 60_000;

const words = (max: number) => z.string().trim().min(1).max(max);

/**
 * The line a version's reason reads as, on the version line and in the history.
 *
 * The count of added questions comes from the version itself rather than from the reason, compared
 * with the version before it, so the line says what that version actually did. Placeholder copy;
 * a version with no reason reads as nothing.
 */
export const rqsVersionReasonText = ({
  reason,
  previous,
  current,
  notes = null,
}: {
  reason: ArtifactVersionReason | null;
  previous: RqsContent | null;
  current: RqsContent;
  /** The version's change notes, which a reconciled version's line is read from. */
  notes?: ArtifactChangeNotes | null | undefined;
}): string => {
  switch (reason) {
    case 'reconciled': {
      return reconciledVersionText(notes);
    }
    case 'drafted_from_goal': {
      return 'Drafted from your goal';
    }
    case 'redrafted_from_changed_goal': {
      return 'Redrafted from your changed goal';
    }
    case 'reworded_by_you': {
      return 'Your wording';
    }
    case 'added_at_your_request': {
      const before = new Set(previous?.items.map((item) => item.id) ?? []);
      const added = current.items.filter((item) => !before.has(item.id)).length;

      return added === 1
        ? 'One question added at your request'
        : `${String(added)} questions added at your request`;
    }
    case 'removed_at_your_request': {
      return 'Removed at your request';
    }
    case 'changed_at_your_request': {
      return 'Changed at your request';
    }
    // Guide reasons: the store never writes one on a set of research questions.
    case 'drafted_from_questions':
    case 'redrafted_from_changed_questions':
    case 'redrafted_for_coverage':
    case 'updated_for_changed_questions':
    case 'reworded_at_your_request':
    case 'reordered_at_your_request':
    case 'image_shown_at_your_request':
    case 'image_removed_at_your_request':
    case 'image_removed_from_materials':
    // Audience reasons: the store never writes one on a set of research questions.
    case 'drafted_from_guide':
    case 'updated_for_changed_guide':
    // Brief reasons: the store never writes one on a set of research questions.
    case 'hidden_by_you':
    case 'shown_by_you':
    case 'reordered_by_you':
    case 'revised_by_sigora':
    case 'updated_from_follow_up':
    case null: {
      return '';
    }
  }
};

/** The study's current chosen Goal, as far as a set of research questions refers to it. */
export interface CurrentGoalRef {
  version: number;
  chosenOptionId: string;
}

/**
 * Research questions must decompose the Goal the study has chosen now.
 *
 * Asked on every write of them, inside the write's transaction, so a set drafted for an earlier
 * Goal — or reworded or reshaped after the Goal changed — is refused rather than stored as though it
 * still described the study. A changed Goal is answered by a redraft on the Questions step, which is
 * the one write that names the new Goal.
 */
export const assertRqsGoalCurrent = (content: RqsContent, goal: CurrentGoalRef | null): void => {
  if (
    goal !== null &&
    content.goal !== null &&
    content.goal.version === goal.version &&
    content.goal.option_id === goal.chosenOptionId
  ) {
    return;
  }

  throw new DomainError(
    'rqs_goal_outdated',
    'These research questions were drafted for an earlier goal. Sigora redrafts them on the Questions step from the goal you chose.',
    { rqs_goal: content.goal, current_goal: goal },
  );
};

/** What of the research questions decides whether they authorize a guide. */
export interface RqsApprovalFact {
  status: ArtifactStatus;
  currentVersion: number;
  approvedVersion: number | null;
  staleness: readonly StalenessWindow[];
}

/**
 * Whether the research questions are approved at the version they hold now, with nothing
 * outstanding — which is what a guide has to be built on before it can be approved.
 */
export const rqsAuthorizeGuide = (rqs: RqsApprovalFact | null): boolean =>
  rqs !== null &&
  rqs.status === 'approved' &&
  rqs.approvedVersion === rqs.currentVersion &&
  rqs.staleness.length === 0;

/**
 * Refuses to return a guide to approved over research questions that do not authorize it.
 *
 * Asked by the guide's approval, and by the requests whose answers it rests on — the coverage check
 * and the Guide card's draft — because a guide is approved against the questions it serves, and
 * questions nobody approved, or that moved since, are not the ones it was checked against. Writing
 * a guide is not refused: a draft authorizes nothing.
 */
export const assertRqsAuthorizeGuide = (rqs: RqsApprovalFact | null): void => {
  if (rqsAuthorizeGuide(rqs)) {
    return;
  }

  throw new DomainError(
    'rqs_not_approved',
    'Approve the research questions first: the guide is approved against the questions it serves.',
    { rqs_status: rqs?.status ?? null },
  );
};

const unknownRq = (rqId: string): DomainError =>
  new DomainError('rq_unknown', 'That question is not one of these research questions.', {
    rq_id: rqId,
  });

/** The refusal a reword or reshape of a set with no Goal reference answers with. */
const setWithoutGoal = (): DomainError =>
  new DomainError(
    'rqs_goal_outdated',
    'These research questions were drafted before they named the goal they came from. Sigora redrafts them on the Questions step from the goal you chose.',
    { rqs_goal: null },
  );

const rewordTextSchema = words(MAX_RQ_TEXT_CHARS);

/**
 * The researcher's rewording of one research question: its text replaced, marked as their wording,
 * and every other field and question left as it was.
 *
 * Wording only, by construction: nothing here can add, remove or re-id a question or touch its
 * decision, so structure cannot change through a wording edit. The same text answers the content
 * unchanged, which the store stores as nothing.
 */
export const planRqReword = ({
  current,
  rqId,
  text,
}: {
  current: RqsContent;
  rqId: string;
  text: string;
}): RqsContent => {
  const parsed = rewordTextSchema.safeParse(text);
  if (!parsed.success) {
    throw new DomainError(
      'invalid_content',
      `A research question is one sentence of at most ${String(MAX_RQ_TEXT_CHARS)} characters.`,
      { kind: 'rqs', issues: z.prettifyError(parsed.error) },
    );
  }

  const target = current.items.find((item) => item.id === rqId);
  if (target === undefined) {
    throw unknownRq(rqId);
  }

  if (current.goal === null) {
    throw setWithoutGoal();
  }

  if (target.text === parsed.data) {
    return current;
  }

  return {
    goal: current.goal,
    items: current.items.map((item) =>
      item.id === rqId ? { ...item, text: parsed.data, worded_by: 'user' } : item,
    ),
  };
};

/** One research question's shape in the drafter's answer format: both keys, and no bound. */
const researchQuestionWireShape = z.strictObject({ text: z.string(), decision: z.string() });

/**
 * The drafter's answer format's shape, as the provider is asked to hold it
 * (`RESEARCH_QUESTIONS_ANSWER_WIRE`). It carries no count, so one wire serves every count asked
 * for: the parser holds the answer to exactly that many.
 */
export const RESEARCH_QUESTIONS_ANSWER_WIRE_SHAPE = z.strictObject({
  questions: z.array(researchQuestionWireShape),
});

/** The wire a research questions draft answers under. */
export const RESEARCH_QUESTIONS_ANSWER_WIRE: AnswerWire = answerWire(
  'research_questions_answer',
  RESEARCH_QUESTIONS_ANSWER_WIRE_SHAPE,
);

/** One research question as the drafter or the reshape writes it: everything but its id. */
export const researchQuestionDraftSchema = researchQuestionWireShape.extend({
  text: words(MAX_RQ_TEXT_CHARS),
  decision: words(MAX_RQ_DECISION_CHARS),
});
export type ResearchQuestionDraft = z.infer<typeof researchQuestionDraftSchema>;

/**
 * What a reshape asks for: questions to add, the ids of questions to remove, and what the
 * researcher asked, as the chat tool hands it through.
 */
export const rqsReshapeInputSchema = z.strictObject({
  add: z.array(researchQuestionDraftSchema),
  remove: z.array(z.uuid()),
  note: words(MAX_RQS_NOTE_CHARS),
});
export type RqsReshapeInput = z.infer<typeof rqsReshapeInputSchema>;

/**
 * The stored set with the named questions removed and new ones appended, and the reason that
 * version is written for.
 *
 * Every question the request did not name is carried over as it is — id, text, decision and whose
 * wording it is — so a reshape cannot reword or re-id anything by accident. New questions get ids
 * from `mintId`, never from the request, and are in Sigora's words. A set past the template's count
 * is not refused: the count is where a study starts, not a cap.
 */
export const planRqsReshape = ({
  current,
  add,
  remove,
  mintId,
}: {
  current: RqsContent;
  add: readonly ResearchQuestionDraft[];
  remove: readonly string[];
  mintId: () => string;
}): { content: RqsContent; reason: ArtifactVersionReason } => {
  if (current.goal === null) {
    throw setWithoutGoal();
  }

  const held = new Set(current.items.map((item) => item.id));
  const unknown = remove.find((id) => !held.has(id));
  if (unknown !== undefined) {
    throw unknownRq(unknown);
  }

  if (add.length === 0 && remove.length === 0) {
    throw new DomainError(
      'invalid_content',
      'Say which research questions to add or remove; this asks for neither.',
      { kind: 'rqs' },
    );
  }

  const removed = new Set(remove);
  const items: ResearchQuestion[] = [
    ...current.items.filter((item) => !removed.has(item.id)),
    ...add.map((draft) => ({
      id: mintId(),
      text: draft.text,
      decision: draft.decision,
      worded_by: 'sigora' as const,
    })),
  ];

  if (items.length === 0) {
    throw new DomainError(
      'invalid_content',
      'That would remove every research question; a study keeps at least one.',
      { kind: 'rqs' },
    );
  }

  const reason: ArtifactVersionReason =
    add.length > 0 && remove.length > 0
      ? 'changed_at_your_request'
      : add.length > 0
        ? 'added_at_your_request'
        : 'removed_at_your_request';

  return { content: { goal: current.goal, items }, reason };
};

/**
 * What a draft was asked from: the chosen Goal it decomposes, the Study Type and template it is
 * shaped by, the confirmed Materials it reads, and the version of the research questions it would
 * write over (`null` for a first draft). A settle writes nothing onto inputs that moved.
 */
export const rqsDraftInputsSchema = z.strictObject({
  goal_version: z.int().positive(),
  goal_option_id: z.uuid(),
  study_type_id: studyTypeIdSchema,
  template_version: z.int().positive(),
  materials_version: z.int().positive(),
  rqs_version: z.int().positive().nullable(),
});
export type RqsDraftInputs = z.infer<typeof rqsDraftInputsSchema>;

export const RQS_DRAFT_STATUSES = ['pending', 'ready', 'unreadable', 'unavailable'] as const;
export type RqsDraftStatus = (typeof RQS_DRAFT_STATUSES)[number];

/**
 * The latest request for drafted research questions.
 *
 * - `pending` — the worker has not answered yet.
 * - `ready` — the worker drafted the questions, which are the study's current version.
 * - `unreadable` — the model answered, and its answer was not the intended number of complete
 *   questions.
 * - `unavailable` — no answer could be had: no model is configured, the day's budget is spent, the
 *   call failed, or the study moved while it was being drafted.
 */
export const rqsDraftRequestSchema = z.strictObject({
  id: z.uuid(),
  status: z.enum(RQS_DRAFT_STATUSES),
  requested_at: z.iso.datetime(),
  inputs: rqsDraftInputsSchema,
});
export type RqsDraftRequest = z.infer<typeof rqsDraftRequestSchema>;

export const RQS_DRAFT_SCHEMA_VERSION = 1;

/**
 * What `studies.rqs_draft` holds: the latest drafting request, or none. The drafted questions
 * themselves are a research-questions version, never held here.
 */
export const storedRqsDraftSchema = z.strictObject({
  schema_version: z.literal(RQS_DRAFT_SCHEMA_VERSION),
  request: rqsDraftRequestSchema.nullable(),
});
export type StoredRqsDraft = z.infer<typeof storedRqsDraftSchema>;

/**
 * The stored request, or nothing. A shape this build cannot read is treated as no request rather
 * than an error: the study's next request replaces it, and refusing to answer over it would take a
 * study down for the sake of a pending line.
 */
export const parseStoredRqsDraft = (stored: unknown): StoredRqsDraft | null => {
  if (stored === null || stored === undefined) {
    return null;
  }

  const parsed = storedRqsDraftSchema.safeParse(stored);

  return parsed.success ? parsed.data : null;
};

/** Whether two sets of inputs describe the same study. */
export const sameRqsDraftInputs = (left: RqsDraftInputs, right: RqsDraftInputs): boolean =>
  left.goal_version === right.goal_version &&
  left.goal_option_id === right.goal_option_id &&
  left.study_type_id === right.study_type_id &&
  left.template_version === right.template_version &&
  left.materials_version === right.materials_version &&
  left.rqs_version === right.rqs_version;

/** As much of a study's research questions as deciding whether to draft them reads. */
export interface RqsDraftFact {
  staleness: readonly StalenessWindow[];
  content: { goal: RqsGoalRef | null };
}

/**
 * Whether the study needs its research questions drafted at all: it has none, the ones it has are
 * waiting on a changed Goal, or they name no Goal at all — a version-1 set, which no reword or
 * reshape will touch and so is only ever redrafted. Never otherwise, so a draft cannot land over
 * questions the researcher has approved or reworded.
 */
export const rqsDraftNeeded = (rqs: RqsDraftFact | null): boolean =>
  rqs === null ||
  rqs.content.goal === null ||
  rqs.staleness.some((window) => window.upstream_kind === 'goal');

/**
 * Whether the study's research questions need reconciling: they are current against the chosen
 * Goal and waiting on changed Materials. Reconciling writes the smallest change that answers it,
 * keeping every question it does not need to touch as it is stored. A set also waiting on a changed
 * Goal is redrafted instead (`rqsDraftNeeded`), and that redraft answers the Materials too.
 */
export const rqsReconcileNeeded = (rqs: RqsDraftFact | null): boolean =>
  rqs !== null && !rqsDraftNeeded(rqs) && rqs.staleness.length > 0;

/**
 * What a request for the research questions is answered with, read off them as they stand:
 * `reconcile` for a set waiting only on changed Materials, `draft` otherwise — a first set, or a
 * redraft over a changed Goal. Never stored: the windows change only when an upstream version does,
 * and every upstream version is in a request's inputs, so the mode is a function of the inputs.
 */
export type RqsDraftMode = 'draft' | 'reconcile';

export const rqsDraftMode = (rqs: RqsDraftFact | null): RqsDraftMode =>
  rqsReconcileNeeded(rqs) ? 'reconcile' : 'draft';

/**
 * Whether a study needs a new request for its current inputs, rather than the one it holds.
 *
 * Only while the research questions need drafting or reconciling (`rqsDraftNeeded`,
 * `rqsReconcileNeeded`), and not while a request for these inputs is pending and younger than
 * `RQS_DRAFT_WAIT_MS`. A pending request older than that is one the step
 * has given up waiting on — its job was lost with a worker, expired, or never ran — and since the
 * queue retries nothing, treating it as still in hand would leave the step waiting on nothing.
 */
export const rqsDraftRequestNeeded = ({
  request,
  inputs,
  rqs,
  now,
}: {
  request: RqsDraftRequest | null;
  inputs: RqsDraftInputs;
  rqs: RqsDraftFact | null;
  now: Date;
}): boolean => {
  if (!rqsDraftNeeded(rqs) && !rqsReconcileNeeded(rqs)) {
    return false;
  }

  const waiting =
    request !== null &&
    request.status === 'pending' &&
    sameRqsDraftInputs(request.inputs, inputs) &&
    now.getTime() - Date.parse(request.requested_at) < RQS_DRAFT_WAIT_MS;

  return !waiting;
};

/** Why one research question of a reconciled set changed, as the reconciler said; see `ChangeNotesInput`. */
export interface RqsChangeReason {
  id: string;
  reason: string;
}

/**
 * How a request is settled: every status but the one it starts in. A `ready` draft carries the
 * drafted questions, which the settle gives ids; a `ready` reconciliation carries the reconciled set
 * itself, built by `toReconciledRqs`, and the reason it gave for each question it changed, added or
 * removed.
 */
export type RqsDraftOutcome =
  | { status: 'ready'; questions: readonly ResearchQuestionDraft[]; reconciled?: undefined }
  | {
      status: 'ready';
      questions?: undefined;
      reconciled: { content: RqsContent; reasons: readonly RqsChangeReason[] };
    }
  | { status: 'unreadable' }
  | { status: 'unavailable' };

/**
 * One research question of a reconciled set, as the reconciler's answer reads once its handles are
 * mapped: the id of the stored question it continues, or `null` for a new one, and its words.
 */
export interface ReconciledRqProposal {
  continues: string | null;
  text: string;
  decision: string;
}

/**
 * The reconciled set a proposal describes, its identity decided here rather than by the model.
 *
 * A question that continues a stored one keeps its id; it keeps whose words it is in while its text
 * is the stored text, and is in Sigora's words once the text changes. A question that continues
 * none is minted an id and is in Sigora's words. The set names `goal`, the study's current Goal. A
 * continued id this set does not hold, or one continued twice, is refused, since either would carry
 * an identity nothing earned.
 */
export const toReconciledRqs = ({
  proposal,
  current,
  goal,
  mintId,
}: {
  proposal: readonly ReconciledRqProposal[];
  current: RqsContent;
  goal: RqsGoalRef;
  mintId: () => string;
}): RqsContent => {
  const held = new Map(current.items.map((item) => [item.id, item]));
  const seen = new Set<string>();

  return {
    goal,
    items: proposal.map((item) => {
      if (item.continues === null) {
        return { id: mintId(), text: item.text, decision: item.decision, worded_by: 'sigora' };
      }

      const kept = held.get(item.continues);
      if (kept === undefined || seen.has(item.continues)) {
        throw new DomainError(
          'invalid_content',
          'A reconciled research question continues one this set does not hold, or one twice.',
          { kind: 'rqs', rq_id: item.continues },
        );
      }
      seen.add(item.continues);

      return {
        id: kept.id,
        text: item.text,
        decision: item.decision,
        worded_by: kept.text === item.text ? kept.worded_by : 'sigora',
      };
    }),
  };
};

/**
 * The job the API process sends. It names the request as well as the study, so a job for a request
 * a later one has replaced is recognised as stale and spends nothing.
 */
export const rqsDraftPayloadSchema = z.object({
  studyId: z.uuid(),
  requestId: z.uuid(),
});
export type RqsDraftPayload = z.infer<typeof rqsDraftPayloadSchema>;

const researchQuestionsAnswerSchema = RESEARCH_QUESTIONS_ANSWER_WIRE_SHAPE.extend({
  questions: z.array(researchQuestionDraftSchema),
});

/**
 * The rules a research questions draft's answer can break, each the reason
 * `parseResearchQuestionsAnswer` refuses it:
 *
 * - `document`: not one JSON document;
 * - `shape`: not the answer format's shape, a field outside its bounds included;
 * - `count`: not exactly the number of questions asked for.
 */
export const RESEARCH_QUESTIONS_ANSWER_RULES = ['document', 'shape', 'count'] as const;

export type ResearchQuestionsAnswerRule = (typeof RESEARCH_QUESTIONS_ANSWER_RULES)[number];

/**
 * The questions a model's answer drafts, or the rule it broke when it is not a complete answer.
 *
 * The whole answer must be one JSON document, `{"questions": [...]}`, optionally inside one code
 * fence, which is the one wrapping a model adds on its own. Exactly `count` questions, each with its
 * text and decision inside their bounds: anything short of that is not an answer, so a partial set
 * can never be stored.
 */
export const parseResearchQuestionsAnswer = (
  answer: string,
  count: number,
): AnswerReading<readonly ResearchQuestionDraft[], ResearchQuestionsAnswerRule> => {
  const read = readAnswerDocument(unfencedAnswer(answer));
  if (read === null) {
    return { ok: false, rule: 'document' };
  }
  const document = read.value;

  const given = answerListLength(document, 'questions');
  if (given !== undefined && given !== count) {
    return { ok: false, rule: 'count', at: 'questions' };
  }
  const parsed = researchQuestionsAnswerSchema.safeParse(document);

  return parsed.success ? { ok: true, value: parsed.data.questions } : refusedShape(parsed.error);
};

const SAMPLE_RESEARCH_QUESTION_DRAFTS: readonly ResearchQuestionDraft[] = [
  {
    text: 'Where in their first week do trial teams stop using the product?',
    decision: 'Which onboarding step to rebuild first.',
  },
  {
    text: 'What did the teams who paid do in their trial that the others did not?',
    decision: 'Which onboarding step to invest in first.',
  },
  {
    text: 'What do trial teams expect to have set up before they invite colleagues?',
    decision: 'What the first-run checklist should ask for.',
  },
  {
    text: 'How do trial teams describe the moment the product became useful to them?',
    decision: 'What the onboarding should lead with.',
  },
];

/**
 * A fixed set of `count` drafted questions, for a stack with no model: the local fixture route and
 * the runtime checks settle these through the real request and settle. Repeats the list when asked
 * for more than it holds, so any template count can be drafted.
 */
export const sampleResearchQuestionDrafts = (count: number): ResearchQuestionDraft[] =>
  Array.from({ length: count }, (_, index) => {
    const draft = SAMPLE_RESEARCH_QUESTION_DRAFTS[
      index % SAMPLE_RESEARCH_QUESTION_DRAFTS.length
    ] as ResearchQuestionDraft;
    const round = Math.floor(index / SAMPLE_RESEARCH_QUESTION_DRAFTS.length);

    return round === 0 ? { ...draft } : { ...draft, text: `${draft.text} (${String(round + 1)})` };
  });
